import { useState, useRef, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Text,
  Group,
  Badge,
  Button,
  Stack,
  Divider,
  Loader,
  Center,
  Alert,
  Textarea,
  ActionIcon,
  Menu,
  Modal,
  Anchor,
  Tooltip,
  Select,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconArrowLeft,
  IconMessageCircle,
  IconCheck,
  IconDots,
  IconEdit,
  IconTrash,
  IconAlertCircle,
  IconUser,
  IconStethoscope,
  IconSend,
  IconLoader,
  IconAlertTriangle,
  IconRefresh,
  IconX,
  IconBuilding,
  IconCalendar,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { useCaseForPathologistReview } from "../../hooks/useWorkflows";
import {
  useCaseDiscussions,
  useCaseComments,
  useAddComment,
  useUpdateComment,
  useDeleteComment,
  useResolveDiscussion,
  useCancelDiscussion,
  useRemoveFailedComment,
} from "../../hooks/useInternalDiscussions";
import {
  discussionCategoryLabels,
  discussionCategoryColors,
  discussionCategoryIcons,
  discussionCategoryAbbreviations,
  DiscussionStatus,
  DiscussionAction,
  type CaseComment,
} from "../../services/internalDiscussions.service";
import { useAuthStore } from "../../store/auth.store";
import { FlagDiscussionModal } from "../../components/workflows/FlagDiscussionModal";
import { DiscussionReasonCard } from "../../components/workflows/DiscussionReasonCard";
import SpecimenHierarchy from "../../components/cases/SpecimenHierarchy";
import { CaseNotesSection } from "../../components/accessioning/CaseNotesSection";
import ErrorsCard from "../../components/accessioning/ErrorsCard";
import { required, scrollToFirstError } from "../../utils/formValidation";
import {
  formatDateOnlyForDisplay,
  getLabTodayDateOnly,
  parseDateOnly,
  type DateOnlyString,
} from "../../utils/dateOnly";
import { LAB_TIME_ZONE } from "../../utils/labTime";

dayjs.extend(relativeTime);

const DISCUSSION_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: LAB_TIME_ZONE,
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

// ==================== TYPES ====================

interface CommentFormValues {
  content: string;
}

interface ResolveFormValues {
  resolution: string;
  consensusDiagnosis: string;
  recommendations: string;
  actionTaken: string; // DiscussionAction enum value or empty
}

interface CancelFormValues {
  reason: string;
}

// Action taken options for resolve modal dropdown (sorted alphabetically)
const actionTakenOptions = [
  {
    value: DiscussionAction.ADDITIONAL_STAINS,
    label: "Additional Stains Ordered",
  },
  { value: DiscussionAction.APPROVED, label: "Approved" },
  {
    value: DiscussionAction.EXTERNAL_CONSULT,
    label: "External Consult Requested",
  },
  { value: DiscussionAction.MODIFIED, label: "Modified" },
];

// ==================== HELPERS ====================

/**
 * Get the primary site name from case specimens
 */
function getSiteName(caseData: any): string {
  const firstSpecimen = caseData?.specimens?.[0];
  if (firstSpecimen?.source?.name) {
    return firstSpecimen.source.name;
  }
  if (firstSpecimen?.subsite?.name) {
    return firstSpecimen.subsite.name;
  }
  if (caseData?.procedureName) {
    return caseData.procedureName;
  }
  if (caseData?.procedure?.name) {
    return caseData.procedure.name;
  }
  return "—";
}

/**
 * Get working diagnosis from first specimen
 */
function getWorkingDiagnosis(caseData: any): string | null {
  const firstSpecimen = caseData?.specimens?.[0];
  if (firstSpecimen?.diagnosis?.diagnosisText) {
    return firstSpecimen.diagnosis.diagnosisText;
  }
  return null;
}

/**
 * Get ordering physician name
 */
function getPhysicianName(caseData: any): string {
  if (caseData?.physician?.fullName) {
    return caseData.physician.fullName;
  }
  if (caseData?.physician?.lastName) {
    return `${caseData.physician.firstName || ""} ${
      caseData.physician.lastName
    }`.trim();
  }
  return "—";
}

/**
 * Get patient display info
 */
export function calculatePatientAge(
  dateOfBirth: DateOnlyString,
  currentDate: DateOnlyString
): number | null {
  const [birthYear, birthMonth, birthDay] = dateOfBirth.split("-").map(Number);
  const [currentYear, currentMonth, currentDay] = currentDate
    .split("-")
    .map(Number);

  if (dateOfBirth > currentDate) return null;

  const birthdayHasOccurred =
    currentMonth > birthMonth ||
    (currentMonth === birthMonth && currentDay >= birthDay);
  return currentYear - birthYear - (birthdayHasOccurred ? 0 : 1);
}

export function formatDiscussionInstantTime(value: string): string {
  const instant = new Date(value);
  return Number.isNaN(instant.getTime())
    ? "—"
    : DISCUSSION_TIME_FORMATTER.format(instant);
}

function getPatientInfo(
  caseData: any,
  labToday: DateOnlyString
): {
  name: string;
  dob: string;
  age: number | null;
  sex: string;
  mrn: string;
} {
  const patient = caseData?.patient;
  if (!patient) {
    return { name: "Unknown Patient", dob: "", age: null, sex: "", mrn: "" };
  }

  const name =
    `${patient.firstName || ""} ${patient.lastName || ""}`.trim() || "Unknown";

  let dob = "";
  let age: number | null = null;
  const dateOfBirth = parseDateOnly(patient.dateOfBirth);
  if (dateOfBirth) {
    dob = formatDateOnlyForDisplay(dateOfBirth);
    age = calculatePatientAge(dateOfBirth, labToday);
  }

  const sex =
    patient.sex === "Male" ? "M" : patient.sex === "Female" ? "F" : "";
  const mrn = patient.medicalRecordNumber || "";

  return { name, dob, age, sex, mrn };
}

// ==================== CHAT MESSAGE COMPONENT ====================

interface ChatMessageProps {
  comment: CaseComment;
  isOwn: boolean;
  onEdit: (comment: CaseComment) => void;
  onDelete: (commentId: string) => void;
  onRetry: (comment: CaseComment) => void;
  onDismiss: (tempId: string) => void;
  isEditing: boolean;
  editForm: ReturnType<typeof useForm<CommentFormValues>>;
  onSaveEdit: (commentId: string) => void;
  onCancelEdit: () => void;
  isUpdating: boolean;
  readOnly?: boolean;
}

function ChatMessage({
  comment,
  isOwn,
  onEdit,
  onDelete,
  onRetry,
  onDismiss,
  isEditing,
  editForm,
  onSaveEdit,
  onCancelEdit,
  isUpdating,
  readOnly = false,
}: ChatMessageProps) {
  const isSending = comment.optimisticStatus === "sending";
  const hasError = comment.optimisticStatus === "error";
  const isTempMessage = !!comment.optimisticTempId;

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: isOwn ? "flex-end" : "flex-start",
        marginBottom: 12,
        opacity: isSending ? 0.7 : 1,
      }}
    >
      {/* Author name for incoming messages */}
      {!isOwn && (
        <Text size="xs" c="dimmed" mb={4} ml={8}>
          {comment.user?.fullName || "Unknown User"}
        </Text>
      )}

      <Group
        gap="xs"
        align="flex-end"
        style={{
          flexDirection: isOwn ? "row-reverse" : "row",
          maxWidth: "80%",
        }}
      >
        {/* Message bubble */}
        <Paper
          p="sm"
          radius="lg"
          style={{
            backgroundColor: isOwn
              ? hasError
                ? "var(--mantine-color-red-1)"
                : "var(--mantine-color-blue-6)"
              : "var(--mantine-color-gray-1)",
            color: isOwn && !hasError ? "white" : "inherit",
            borderBottomRightRadius: isOwn ? 4 : undefined,
            borderBottomLeftRadius: !isOwn ? 4 : undefined,
            maxWidth: "100%",
          }}
        >
          {isEditing ? (
            <Stack gap="xs">
              <Textarea
                {...editForm.getInputProps("content")}
                minRows={2}
                autoFocus
                styles={{
                  input: {
                    backgroundColor: "white",
                    color: "var(--mantine-color-dark-9)",
                  },
                }}
              />
              <Group gap="xs" justify="flex-end">
                <Button
                  size="xs"
                  variant="subtle"
                  color={isOwn ? "white" : "gray"}
                  onClick={onCancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  size="xs"
                  variant="filled"
                  color="white"
                  c="blue"
                  onClick={() => onSaveEdit(comment.id)}
                  loading={isUpdating}
                >
                  Save
                </Button>
              </Group>
            </Stack>
          ) : (
            <Text
              size="sm"
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {comment.content}
            </Text>
          )}
        </Paper>

        {/* Status icons and menu for own messages */}
        {isOwn && !isEditing && (
          <Group gap={4}>
            {isSending && (
              <Tooltip label="Sending...">
                <IconLoader
                  size={14}
                  style={{ animation: "spin 1s linear infinite" }}
                />
              </Tooltip>
            )}
            {hasError && (
              <Group gap={4}>
                <Tooltip label="Failed to send">
                  <IconAlertTriangle size={14} color="red" />
                </Tooltip>
                <Tooltip label="Retry">
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    color="blue"
                    onClick={() => onRetry(comment)}
                  >
                    <IconRefresh size={12} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Dismiss">
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    color="red"
                    onClick={() => onDismiss(comment.optimisticTempId!)}
                  >
                    <IconX size={12} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            )}
            {!isTempMessage && !isSending && !hasError && !readOnly && (
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon variant="subtle" size="xs" color="gray">
                    <IconDots size={14} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconEdit size={14} />}
                    onClick={() => onEdit(comment)}
                  >
                    Edit
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconTrash size={14} />}
                    color="red"
                    onClick={() => onDelete(comment.id)}
                  >
                    Delete
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        )}
      </Group>

      {/* Timestamp */}
      <Text
        size="xs"
        c="dimmed"
        mt={4}
        style={{ marginLeft: isOwn ? 0 : 8, marginRight: isOwn ? 8 : 0 }}
      >
        {hasError
          ? "Failed to send"
          : isSending
          ? "Sending..."
          : formatDiscussionInstantTime(comment.createdAt)}
      </Text>
    </Box>
  );
}

// ==================== MAIN COMPONENT ====================

export default function CaseDiscussionPage() {
  const { caseId, discussionId } = useParams<{
    caseId: string;
    discussionId?: string;
  }>();
  const navigate = useNavigate();
  const profile = useAuthStore((state) => state.profile);
  const labToday = useMemo(() => getLabTodayDateOnly(), []);

  // Modal states
  const [flagModalOpened, flagModalHandlers] = useDisclosure(false);
  const [resolveModalOpened, resolveModalHandlers] = useDisclosure(false);
  const [cancelModalOpened, cancelModalHandlers] = useDisclosure(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);

  // Ref for auto-scroll
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  // Data queries
  const {
    data: caseData,
    isLoading: caseLoading,
    error: caseError,
  } = useCaseForPathologistReview(caseId);

  const { data: discussions = [], isLoading: discussionsLoading } =
    useCaseDiscussions(caseId || "");

  const { data: comments = [], isLoading: commentsLoading } = useCaseComments(
    caseId || ""
  );

  // Mutations - pass current user for optimistic updates
  const addComment = useAddComment(
    profile ? { id: profile.id, fullName: profile.fullName } : undefined
  );
  const updateComment = useUpdateComment();
  const deleteComment = useDeleteComment();
  const resolveDiscussion = useResolveDiscussion();
  const cancelDiscussion = useCancelDiscussion();
  const removeFailedComment = useRemoveFailedComment();

  // Active discussion (first pending) - used for actions like resolve
  const activeDiscussion = discussions.find(
    (d) => d.status === DiscussionStatus.PENDING
  );

  // Discussion to display (either specified by URL or active pending)
  const displayedDiscussion = useMemo(() => {
    if (discussionId) {
      return discussions.find((d) => d.id === discussionId);
    }
    return activeDiscussion;
  }, [discussions, discussionId, activeDiscussion]);

  // Is this a read-only view (resolved discussion)?
  const isReadOnly = displayedDiscussion?.status === DiscussionStatus.RESOLVED;

  // Filter comments to only show those for the displayed discussion
  const displayedComments = useMemo(
    () =>
      displayedDiscussion
        ? comments.filter(
            (c) => c.internalDiscussionId === displayedDiscussion.id
          )
        : [],
    [displayedDiscussion, comments]
  );

  // Forms
  const commentForm = useForm<CommentFormValues>({
    initialValues: { content: "" },
    validate: {
      content: required("Message is required"),
    },
  });

  const resolveForm = useForm<ResolveFormValues>({
    initialValues: {
      resolution: "",
      consensusDiagnosis: "",
      recommendations: "",
      actionTaken: "",
    },
    // All fields are optional per C-807 spec
  });

  // Inline edit form
  const editForm = useForm<CommentFormValues>({
    initialValues: { content: "" },
    validate: {
      content: required("Message is required"),
    },
  });

  // Cancel discussion form
  const cancelForm = useForm<CancelFormValues>({
    initialValues: { reason: "" },
  });

  // Loading state
  const isLoading = caseLoading || discussionsLoading || commentsLoading;

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (shouldAutoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [displayedComments, shouldAutoScroll]);

  // Track if user has scrolled up
  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        chatContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShouldAutoScroll(isNearBottom);
    }
  };

  // Handlers
  const handleBackToQueue = () => {
    navigate("/discussion-queue");
  };

  const handleEditDiagnosis = () => {
    navigate(`/pathologist-review/${caseId}`);
  };

  const handleAddComment = commentForm.onSubmit(
    async (values) => {
      if (!caseId) return;

      try {
        await addComment.mutateAsync({
          caseId,
          content: values.content.trim(),
          discussionId: activeDiscussion?.id,
          isInternal: true,
        });

        commentForm.reset();
        setShouldAutoScroll(true);
      } catch {
        // Error is handled by optimistic update - message shows as failed
      }
    },
    () => scrollToFirstError(commentForm)
  );

  // Handle Enter to send (Shift+Enter for new line)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAddComment(e as any);
    }
  };

  const handleEditComment = async (commentId: string) => {
    const error = editForm.validate();
    if (error.hasErrors) {
      scrollToFirstError(editForm);
      return;
    }

    try {
      await updateComment.mutateAsync({
        commentId,
        data: { content: editForm.values.content.trim() },
      });

      setEditingCommentId(null);
      editForm.reset();
      notifications.show({
        title: "Message Updated",
        message: "Your message has been updated",
        color: "green",
      });
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to update message",
        color: "red",
      });
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm("Are you sure you want to delete this message?")) return;

    try {
      await deleteComment.mutateAsync(commentId);
      notifications.show({
        title: "Message Deleted",
        message: "Message has been deleted",
        color: "green",
      });
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to delete message",
        color: "red",
      });
    }
  };

  const startEditing = (comment: CaseComment) => {
    setEditingCommentId(comment.id);
    editForm.setFieldValue("content", comment.content);
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    editForm.reset();
  };

  const handleRetryComment = async (comment: CaseComment) => {
    if (!caseId || !comment.optimisticTempId) return;

    // Remove the failed message first
    removeFailedComment(caseId, comment.optimisticTempId);

    // Retry sending
    try {
      await addComment.mutateAsync({
        caseId,
        content: comment.content,
        discussionId: activeDiscussion?.id,
        isInternal: true,
      });
    } catch {
      // Error handled by optimistic update
    }
  };

  const handleDismissFailedComment = (tempId: string) => {
    if (caseId) {
      removeFailedComment(caseId, tempId);
    }
  };

  const handleResolve = resolveForm.onSubmit(async (values) => {
    if (!activeDiscussion) return;

    try {
      await resolveDiscussion.mutateAsync({
        discussionId: activeDiscussion.id,
        data: {
          resolution: values.resolution.trim() || undefined,
          consensusDiagnosis: values.consensusDiagnosis.trim() || undefined,
          recommendations: values.recommendations.trim() || undefined,
          actionTaken: (values.actionTaken as DiscussionAction) || undefined,
        },
      });

      resolveForm.reset();
      resolveModalHandlers.close();
      notifications.show({
        title: "Discussion Resolved",
        message: "The discussion has been marked as resolved",
        color: "green",
      });

      // Navigate back to queue after resolving
      navigate("/discussion-queue");
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to resolve discussion",
        color: "red",
      });
    }
  });

  const handleCancel = async () => {
    if (!activeDiscussion) return;

    try {
      await cancelDiscussion.mutateAsync({
        discussionId: activeDiscussion.id,
        data: {
          reason: cancelForm.values.reason.trim() || undefined,
        },
      });

      cancelModalHandlers.close();
      cancelForm.reset();
      notifications.show({
        title: "Discussion Cancelled",
        message: "The discussion has been cancelled",
        color: "blue",
      });

      // Navigate back to queue after cancelling
      navigate("/discussion-queue");
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to cancel discussion",
        color: "red",
      });
    }
  };

  // Error state
  if (caseError) {
    return (
      <Box p="xl" maw={600} mx="auto">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Case"
          color="red"
        >
          {(caseError as any)?.message || "Failed to load case data"}
        </Alert>
        <Button
          mt="md"
          onClick={handleBackToQueue}
          leftSection={<IconArrowLeft size={16} />}
        >
          Back to Queue
        </Button>
      </Box>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Center h="calc(100vh - 76px)">
        <Loader size="lg" />
      </Center>
    );
  }

  const patientInfo = getPatientInfo(caseData, labToday);
  const siteName = getSiteName(caseData);
  const physicianName = getPhysicianName(caseData);
  const workingDiagnosis = getWorkingDiagnosis(caseData);

  return (
    <>
      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <Grid gutter={0} style={{ margin: "0 auto", maxWidth: "2200px" }}>
        {/* LEFT SIDEBAR - Case Info, Notes, Errors */}
        <Grid.Col span={{ base: 12, lg: 2 }} style={{ minWidth: 300 }}>
          <Box
            style={{
              height: "calc(100vh - 76px)",
              display: "flex",
              flexDirection: "column",
              borderRight: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            {/* Fixed Header - Back to Queue */}
            <Box
              p="md"
              style={{
                borderBottom: "1px solid var(--mantine-color-gray-3)",
                backgroundColor: "white",
                height: 60,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Button
                variant="subtle"
                leftSection={<IconArrowLeft size={16} />}
                onClick={handleBackToQueue}
                size="sm"
              >
                Back to Queue
              </Button>
            </Box>

            {/* Scrollable Content */}
            <Box
              style={{
                flex: 1,
                overflowY: "auto",
              }}
              p="md"
            >
              <Stack gap="md">
                {/* Case Info Card */}
                <Paper withBorder p="md">
                  <Stack gap="sm">
                    {/* Case Number as Link */}
                    <Group justify="space-between" align="flex-start">
                      <div>
                        <Anchor
                          component={Link}
                          to={`/cases/${caseId}`}
                          size="lg"
                          fw={600}
                        >
                          {caseData?.caseNumber || "—"}
                        </Anchor>
                        <Group gap={6} mt={4}>
                          {caseData?.isRush && (
                            <Tooltip
                              label={
                                caseData?.rushReason ||
                                "Rush case — expedited processing"
                              }
                              multiline
                              maw={300}
                            >
                              <Badge color="red" size="xs">
                                RUSH
                              </Badge>
                            </Tooltip>
                          )}
                          {caseData?.isHighPriority && (
                            <Tooltip
                              label={
                                caseData?.highPriorityReason ||
                                "High priority — flagged for attention"
                              }
                              multiline
                              maw={300}
                            >
                              <Badge color="orange" size="xs">
                                PRIORITY
                              </Badge>
                            </Tooltip>
                          )}
                          <Badge variant="light" size="xs">
                            {caseData?.currentStatus?.name || "Unknown"}
                          </Badge>
                        </Group>
                      </div>
                    </Group>

                    <Divider />

                    {/* Patient Info */}
                    <div>
                      <Group gap="xs" mb={4}>
                        <IconUser size={14} color="gray" />
                        <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                          Patient
                        </Text>
                      </Group>
                      <Text fw={500} size="sm">
                        {patientInfo.name}
                      </Text>
                      {(patientInfo.dob || patientInfo.mrn) && (
                        <Text size="xs" c="dimmed">
                          {patientInfo.age !== null &&
                            `${patientInfo.age}${patientInfo.sex}`}
                          {patientInfo.dob && ` • DOB: ${patientInfo.dob}`}
                          {patientInfo.mrn && ` • MRN: ${patientInfo.mrn}`}
                        </Text>
                      )}
                    </div>

                    {/* Procedure/Site */}
                    <div>
                      <Group gap="xs" mb={4}>
                        <IconCalendar size={14} color="gray" />
                        <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                          Procedure / Site
                        </Text>
                      </Group>
                      <Text size="sm">{siteName}</Text>
                    </div>

                    {/* Pathologist */}
                    {caseData?.pathologist && (
                      <div>
                        <Group gap="xs" mb={4}>
                          <IconStethoscope size={14} color="gray" />
                          <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                            Pathologist
                          </Text>
                        </Group>
                        <Text size="sm">{caseData.pathologist.fullName}</Text>
                      </div>
                    )}

                    {/* Ordering Physician */}
                    <div>
                      <Group gap="xs" mb={4}>
                        <IconUser size={14} color="gray" />
                        <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                          Ordering Physician
                        </Text>
                      </Group>
                      <Text size="sm">{physicianName}</Text>
                    </div>

                    {/* Facility */}
                    {caseData?.facility && (
                      <div>
                        <Group gap="xs" mb={4}>
                          <IconBuilding size={14} color="gray" />
                          <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                            Facility
                          </Text>
                        </Group>
                        <Text size="sm">{caseData.facility.name}</Text>
                      </div>
                    )}

                    {/* Working Diagnosis */}
                    {workingDiagnosis && (
                      <div>
                        <Text
                          size="xs"
                          c="dimmed"
                          tt="uppercase"
                          fw={500}
                          mb={4}
                        >
                          Working Diagnosis
                        </Text>
                        <Paper p="xs" bg="blue.0" radius="sm">
                          {/* eslint-disable-next-line react/no-danger */}
                          <Text
                            size="xs"
                            style={{ whiteSpace: "pre-wrap" }}
                            lineClamp={4}
                            dangerouslySetInnerHTML={{
                              __html: workingDiagnosis,
                            }}
                          />
                        </Paper>
                      </div>
                    )}

                    <Divider />

                    {/* Edit Diagnosis Button - Only visible when case is in Pathologist Review */}
                    {caseData?.currentStatus?.name === "Pathologist Review" && (
                      <Button
                        variant="light"
                        size="sm"
                        leftSection={<IconEdit size={14} />}
                        onClick={handleEditDiagnosis}
                        fullWidth
                      >
                        Edit Diagnosis
                      </Button>
                    )}
                  </Stack>
                </Paper>

                {displayedDiscussion?.reason && (
                  <DiscussionReasonCard reason={displayedDiscussion.reason} />
                )}

                {/* Notes Section */}
                <CaseNotesSection caseId={caseId || null} />

                {/* Errors Section */}
                <ErrorsCard caseId={caseId || null} />
              </Stack>
            </Box>
          </Box>
        </Grid.Col>

        {/* CENTER COLUMN - Discussion / Chat */}
        <Grid.Col span={{ base: 12, lg: "auto" }} style={{ flex: 1 }}>
          <Box
            style={{
              height: "calc(100vh - 76px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header Bar - Fixed, not scrollable */}
            <Box
              p="md"
              style={{
                borderBottom: "1px solid var(--mantine-color-gray-3)",
                backgroundColor: "white",
                height: 60,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Group gap="md">
                <IconMessageCircle size={24} />
                <Text fw={600} size="lg">
                  Discussion
                </Text>

                {/* Category Badge */}
                {displayedDiscussion?.category && (
                  <Badge
                    color={
                      discussionCategoryColors[displayedDiscussion.category]
                    }
                    variant="light"
                    size="lg"
                    leftSection={
                      discussionCategoryIcons[displayedDiscussion.category]
                    }
                  >
                    {
                      discussionCategoryAbbreviations[
                        displayedDiscussion.category
                      ]
                    }
                  </Badge>
                )}

                {/* Resolved Badge */}
                {isReadOnly && (
                  <Badge color="green" variant="light" size="lg">
                    RESOLVED
                  </Badge>
                )}

                {displayedDiscussion?.isUrgent && !isReadOnly && (
                  <Badge color="red" size="sm">
                    URGENT
                  </Badge>
                )}

                {/* Flagged by / Resolved by info */}
                {displayedDiscussion && (
                  <Text size="sm" c="dimmed">
                    {isReadOnly
                      ? `Resolved by ${
                          displayedDiscussion.resolvedBy?.fullName || "Unknown"
                        } ${dayjs(displayedDiscussion.resolvedAt).fromNow()}`
                      : `Flagged by ${
                          displayedDiscussion.initiatedBy?.fullName || "Unknown"
                        } ${dayjs(displayedDiscussion.createdAt).fromNow()}`}
                  </Text>
                )}
              </Group>
            </Box>

            {/* Chat Messages - Scrollable area */}
            <Box
              ref={chatContainerRef}
              onScroll={handleScroll}
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "var(--mantine-spacing-md)",
              }}
            >
              {/* No Discussion State - only when no displayedDiscussion */}
              {!displayedDiscussion && (
                <Center h="100%">
                  <Stack align="center" gap="md">
                    <Alert
                      color="blue"
                      variant="light"
                      title="No Active Discussion"
                      icon={<IconMessageCircle size={16} />}
                    >
                      There is no active discussion for this case.
                    </Alert>
                    <Button
                      variant="light"
                      leftSection={<IconArrowLeft size={16} />}
                      onClick={handleBackToQueue}
                    >
                      Back to Discussion Queue
                    </Button>
                  </Stack>
                </Center>
              )}

              {/* Resolution banner for resolved discussions */}
              {isReadOnly && displayedDiscussion?.resolution && (
                <Alert
                  color="green"
                  variant="light"
                  icon={<IconCheck size={16} />}
                  mb="md"
                >
                  <Text size="sm" fw={500}>
                    Resolution:
                  </Text>
                  <Text size="sm">{displayedDiscussion.resolution}</Text>
                </Alert>
              )}

              {/* Messages List */}
              {displayedDiscussion && displayedComments.length === 0 && (
                <Center py="xl">
                  <Text c="dimmed" size="sm">
                    {isReadOnly
                      ? "No messages in this discussion."
                      : "No messages yet. Start the conversation!"}
                  </Text>
                </Center>
              )}

              {displayedComments.map((comment) => (
                <ChatMessage
                  key={comment.optimisticTempId || comment.id}
                  comment={comment}
                  isOwn={comment.userId === profile?.id}
                  onEdit={startEditing}
                  onDelete={handleDeleteComment}
                  onRetry={handleRetryComment}
                  onDismiss={handleDismissFailedComment}
                  isEditing={editingCommentId === comment.id}
                  editForm={editForm}
                  onSaveEdit={handleEditComment}
                  onCancelEdit={cancelEditing}
                  isUpdating={updateComment.isPending}
                  readOnly={isReadOnly}
                />
              ))}

              {/* Scroll anchor */}
              <div ref={messagesEndRef} />
            </Box>

            {/* Chat Input - Fixed at bottom */}
            <Box
              p="md"
              style={{
                borderTop: "1px solid var(--mantine-color-gray-3)",
                backgroundColor: "white",
              }}
            >
              {isReadOnly ? (
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">
                    This discussion has been resolved. You cannot send new
                    messages.
                  </Text>
                  {activeDiscussion ? (
                    <Button
                      variant="light"
                      size="sm"
                      leftSection={<IconMessageCircle size={14} />}
                      onClick={() => navigate(`/discussion-queue/${caseId}`)}
                    >
                      Continue Active Discussion
                    </Button>
                  ) : (
                    <Button
                      variant="light"
                      size="sm"
                      leftSection={<IconMessageCircle size={14} />}
                      onClick={flagModalHandlers.open}
                    >
                      Start New Discussion
                    </Button>
                  )}
                </Stack>
              ) : activeDiscussion ? (
                <form onSubmit={handleAddComment}>
                  <Group align="flex-end" gap="sm">
                    <Textarea
                      placeholder="Type a message... (Enter to send, Shift+Enter for new line)"
                      {...commentForm.getInputProps("content")}
                      minRows={1}
                      maxRows={4}
                      autosize
                      onKeyDown={handleKeyDown}
                      style={{ flex: 1 }}
                      styles={{
                        input: {
                          borderRadius: 20,
                          paddingLeft: 16,
                          paddingRight: 16,
                        },
                      }}
                    />
                    <ActionIcon
                      type="submit"
                      size="lg"
                      radius="xl"
                      variant="filled"
                      loading={addComment.isPending}
                      disabled={!commentForm.values.content.trim()}
                    >
                      <IconSend size={18} />
                    </ActionIcon>
                  </Group>
                  <Text size="xs" c="dimmed" mt="xs" ta="center">
                    Messages are internal and not part of the final report
                  </Text>
                </form>
              ) : (
                <Text size="sm" c="dimmed" ta="center">
                  Start a discussion to enable messaging
                </Text>
              )}
            </Box>
          </Box>
        </Grid.Col>

        {/* RIGHT SIDEBAR - Resolve Button, Specimens */}
        <Grid.Col span={{ base: 12, lg: 2 }} style={{ minWidth: 320 }}>
          <Box
            style={{
              height: "calc(100vh - 76px)",
              display: "flex",
              flexDirection: "column",
              borderLeft: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            {/* Fixed Header - Action Buttons */}
            {!isReadOnly && (
              <Box
                p="md"
                style={{
                  borderBottom: "1px solid var(--mantine-color-gray-3)",
                  backgroundColor: "white",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {/* Resolve Discussion Button */}
                <Button
                  color="green"
                  leftSection={<IconCheck size={16} />}
                  onClick={resolveModalHandlers.open}
                  fullWidth
                  disabled={!activeDiscussion}
                >
                  Resolve Discussion
                </Button>
                {/* Cancel Discussion Button */}
                <Button
                  color="red"
                  variant="outline"
                  leftSection={<IconX size={16} />}
                  onClick={cancelModalHandlers.open}
                  fullWidth
                  disabled={!activeDiscussion}
                >
                  Cancel Discussion
                </Button>
              </Box>
            )}

            {/* Scrollable Content - Specimens */}
            <Box
              style={{
                flex: 1,
                overflowY: "auto",
              }}
              p="md"
            >
              <Stack gap="md">
                {/* Flag for Discussion Button - Only when no active discussion and not viewing resolved */}
                {!activeDiscussion && !isReadOnly && (
                  <Button
                    leftSection={<IconMessageCircle size={16} />}
                    onClick={flagModalHandlers.open}
                    fullWidth
                    variant="light"
                  >
                    Flag for Discussion
                  </Button>
                )}

                {/* Specimens Section */}
                {caseData?.specimens && caseData.specimens.length > 0 && (
                  <SpecimenHierarchy
                    specimens={caseData.specimens}
                    variant="default"
                  />
                )}
              </Stack>
            </Box>
          </Box>
        </Grid.Col>
      </Grid>

      {/* Flag Discussion Modal */}
      <FlagDiscussionModal
        opened={flagModalOpened}
        onClose={flagModalHandlers.close}
        caseId={caseId || ""}
        caseNumber={caseData?.caseNumber || ""}
        workingDiagnosis={workingDiagnosis || undefined}
      />

      {/* Resolve Discussion Modal */}
      <Modal
        opened={resolveModalOpened}
        onClose={resolveModalHandlers.close}
        closeOnClickOutside={false}
        title={
          <Group gap="xs">
            <IconCheck size={20} />
            <span>Resolve Discussion</span>
          </Group>
        }
        size="lg"
        centered
      >
        <form onSubmit={handleResolve}>
          <Stack gap="md">
            {activeDiscussion && (
              <div>
                <Text size="sm" fw={500}>
                  Case: {caseData?.caseNumber || "—"}
                </Text>
                {activeDiscussion.category && (
                  <Group gap="xs" mt="xs">
                    <Badge
                      color={
                        discussionCategoryColors[activeDiscussion.category]
                      }
                      variant="light"
                      leftSection={
                        discussionCategoryIcons[activeDiscussion.category]
                      }
                    >
                      {discussionCategoryLabels[activeDiscussion.category]}
                    </Badge>
                  </Group>
                )}
              </div>
            )}

            <Divider />

            <Select
              label="Action Taken"
              placeholder="Select action taken..."
              data={actionTakenOptions}
              clearable
              {...resolveForm.getInputProps("actionTaken")}
            />

            <Textarea
              label="Consensus Diagnosis"
              placeholder="Enter the agreed-upon diagnosis..."
              minRows={2}
              {...resolveForm.getInputProps("consensusDiagnosis")}
            />

            <Textarea
              label="Recommendations"
              placeholder="Enter any follow-up recommendations..."
              minRows={2}
              {...resolveForm.getInputProps("recommendations")}
            />

            <Textarea
              label="Resolution Notes"
              placeholder="Describe the outcome of this discussion..."
              minRows={3}
              {...resolveForm.getInputProps("resolution")}
            />

            <Group justify="space-between">
              <Button variant="subtle" onClick={resolveModalHandlers.close}>
                Cancel
              </Button>
              <Button
                type="submit"
                color="green"
                leftSection={<IconCheck size={16} />}
                loading={resolveDiscussion.isPending}
              >
                Resolve
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Cancel Discussion Confirmation Dialog */}
      <Modal
        opened={cancelModalOpened}
        onClose={cancelModalHandlers.close}
        closeOnClickOutside={false}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={20} color="var(--mantine-color-red-6)" />
            <span>Cancel Discussion</span>
          </Group>
        }
        size="lg"
        centered
      >
        <Stack gap="md">
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertTriangle size={16} />}
          >
            Are you sure you want to cancel this discussion? This action cannot
            be undone.
          </Alert>

          <Textarea
            label="Reason for Cancellation (optional)"
            placeholder="Why is this discussion no longer needed?"
            minRows={3}
            {...cancelForm.getInputProps("reason")}
          />

          <Group justify="space-between">
            <Button variant="default" onClick={cancelModalHandlers.close}>
              Keep Discussion
            </Button>
            <Button
              color="red"
              onClick={handleCancel}
              loading={cancelDiscussion.isPending}
              leftSection={<IconX size={16} />}
            >
              Yes, Cancel Discussion
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
